import { ErrorState, LoadingState } from '@playforge/ui';
import {
  Badge,
  BlockStack,
  Banner,
  Button,
  ButtonGroup,
  Card,
  ChoiceList,
  FormLayout,
  InlineGrid,
  InlineStack,
  List,
  Page,
  Select,
  Tabs,
  Text,
  TextField,
  Toast,
} from '@shopify/polaris';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { apiClient, ApiError } from '../lib/api-client';
import { useApi } from '../lib/use-api';
import { GAME_EMOJI, GAME_LABELS } from '../components/GamePreview';
import {
  type AttemptFrequencyId,
  type CampaignView,
  type CustomerTypeId,
  type GameTypeId,
  type GameTypeOption,
  type GameView,
  REWARD_TYPE_OPTIONS,
  type RewardTypeId,
  type RewardView,
  type RulesConfig,
  type RulesView,
  VALUE_REWARD_TYPES,
} from '../lib/types';

export function CampaignBuilderPage(): JSX.Element {
  const { id = '' } = useParams();
  const navigate = useNavigate();

  const campaign = useApi(() => apiClient.get<CampaignView>(`/campaigns/${id}`), [id]);
  const gameTypes = useApi(() => apiClient.get<GameTypeOption[]>('/games/types'), []);
  const rewards = useApi(() => apiClient.get<RewardView[]>(`/rewards?campaignId=${id}`), [id]);

  const [tab, setTab] = useState(0);
  const [toast, setToast] = useState<string | null>(null);

  if (campaign.loading) {
    return (
      <Page title="Campaign">
        <LoadingState label="Loading campaign" />
      </Page>
    );
  }
  if (campaign.error || !campaign.data) {
    return (
      <Page title="Campaign" backAction={{ content: 'Campaigns', onAction: () => navigate('/campaigns') }}>
        <ErrorState message={campaign.error?.message ?? 'Campaign not found'} onRetry={campaign.refetch} />
      </Page>
    );
  }

  const data = campaign.data;
  const tabs = [
    { id: 'details', content: 'Details' },
    { id: 'game', content: 'Game' },
    { id: 'rewards', content: 'Rewards' },
    { id: 'rules', content: 'Rules' },
    { id: 'schedule', content: 'Schedule' },
    { id: 'publish', content: 'Publish' },
  ];

  return (
    <Page
      title={data.campaignName}
      titleMetadata={
        <Badge {...(data.status === 'published' ? { tone: 'success' as const } : {})}>
          {data.status}
        </Badge>
      }
      backAction={{ content: 'Campaigns', onAction: () => navigate('/campaigns') }}
    >
      <Tabs tabs={tabs} selected={tab} onSelect={setTab}>
        <div style={{ paddingTop: 16 }}>
          {tab === 0 ? <DetailsTab campaign={data} onSaved={(m) => { setToast(m); campaign.refetch(); }} /> : null}
          {tab === 1 ? (
            <GameTab
              campaign={data}
              gameTypes={gameTypes.data ?? []}
              onSaved={(m) => { setToast(m); campaign.refetch(); }}
            />
          ) : null}
          {tab === 2 ? (
            <RewardsTab
              campaignId={id}
              rewards={rewards.data ?? []}
              loading={rewards.loading}
              onChanged={(m) => { setToast(m); rewards.refetch(); }}
            />
          ) : null}
          {tab === 3 ? (
            <RulesTab campaignId={id} onSaved={(m) => setToast(m)} />
          ) : null}
          {tab === 4 ? (
            <ScheduleTab campaign={data} onSaved={(m) => { setToast(m); campaign.refetch(); }} />
          ) : null}
          {tab === 5 ? (
            <PublishTab
              campaign={data}
              rewardCount={rewards.data?.length ?? 0}
              onPublished={(m) => { setToast(m); campaign.refetch(); }}
            />
          ) : null}
        </div>
      </Tabs>
      {toast ? <Toast content={toast} onDismiss={() => setToast(null)} /> : null}
    </Page>
  );
}

function DetailsTab({ campaign, onSaved }: { campaign: CampaignView; onSaved: (m: string) => void }): JSX.Element {
  const [name, setName] = useState(campaign.campaignName);
  const [description, setDescription] = useState(campaign.description ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      await apiClient.put<CampaignView>(`/campaigns/${campaign.id}`, {
        campaignName: name,
        ...(description ? { description } : {}),
      });
      onSaved('Details saved');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <BlockStack gap="400">
        {error ? <ErrorState message={error} /> : null}
        <FormLayout>
          <TextField label="Campaign name" value={name} onChange={setName} autoComplete="off" />
          <TextField
            label="Description"
            value={description}
            onChange={setDescription}
            autoComplete="off"
            multiline={3}
          />
        </FormLayout>
        <Button variant="primary" loading={busy} onClick={() => void save()}>
          Save details
        </Button>
      </BlockStack>
    </Card>
  );
}

function GameTab({
  campaign,
  gameTypes,
  onSaved,
}: {
  campaign: CampaignView;
  gameTypes: GameTypeOption[];
  onSaved: (m: string) => void;
}): JSX.Element {
  const current = useApi(
    () => (campaign.gameId ? apiClient.get<GameView>(`/games/${campaign.gameId}`) : Promise.resolve(null)),
    [campaign.gameId],
  );
  // `picked` is the user's choice this session; until they pick, reflect the
  // saved game (so the UI shows the real current game, not a hard-coded default).
  const [picked, setPicked] = useState<GameTypeId | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const savedType = current.data?.type ?? null;
  const selected: GameTypeId = picked ?? savedType ?? (gameTypes[0]?.type as GameTypeId) ?? 'spin_wheel';
  const dirty = selected !== savedType;

  const setGame = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      const game = await apiClient.post<GameView>('/games', { type: selected });
      await apiClient.put<CampaignView>(`/campaigns/${campaign.id}`, { gameId: game.id });
      setPicked(null);
      current.refetch();
      onSaved(`Game set to ${GAME_LABELS[selected]}`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not set game');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <BlockStack gap="400">
        {error ? <ErrorState message={error} /> : null}
        <Text as="p" tone={savedType ? 'success' : 'subdued'}>
          {savedType
            ? `Current game on the storefront: ${GAME_LABELS[savedType]}`
            : 'No game selected yet — pick one below.'}
        </Text>
        <InlineGrid columns={{ xs: 2, sm: 3 }} gap="300">
          {gameTypes.map((g) => {
            const isActive = selected === g.type;
            return (
              <button
                key={g.type}
                type="button"
                onClick={() => setPicked(g.type as GameTypeId)}
                style={{
                  cursor: 'pointer',
                  textAlign: 'center',
                  padding: '18px 10px',
                  borderRadius: 12,
                  background: isActive ? 'var(--p-color-bg-surface-selected, #f1f1ff)' : 'var(--p-color-bg-surface, #fff)',
                  border: isActive
                    ? '2px solid var(--p-color-border-emphasis, #5c6ac4)'
                    : '1px solid var(--p-color-border, #e1e3e5)',
                }}
              >
                <div style={{ fontSize: 30, lineHeight: 1.2 }}>{GAME_EMOJI[g.type as GameTypeId] ?? '🎮'}</div>
                <div style={{ fontWeight: 600, fontSize: 13, marginTop: 6 }}>{g.name}</div>
              </button>
            );
          })}
        </InlineGrid>
        <InlineStack align="start">
          <Button variant="primary" loading={busy} disabled={!dirty && Boolean(savedType)} onClick={() => void setGame()}>
            {savedType ? 'Update game' : 'Set game'}
          </Button>
        </InlineStack>
      </BlockStack>
    </Card>
  );
}

function RewardsTab({
  campaignId,
  rewards,
  loading,
  onChanged,
}: {
  campaignId: string;
  rewards: RewardView[];
  loading: boolean;
  onChanged: (m: string) => void;
}): JSX.Element {
  const [type, setType] = useState<RewardTypeId>('percentage');
  const [value, setValue] = useState('10');
  const [weight, setWeight] = useState('1');
  const [expiryDays, setExpiryDays] = useState('');
  const [productId, setProductId] = useState<string | null>(null);
  const [productTitle, setProductTitle] = useState<string | null>(null);
  const [productImage, setProductImage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const needsValue = VALUE_REWARD_TYPES.includes(type);
  const needsProduct = type === 'free_product';
  // Only discount-bearing rewards mint a redeemable code that can expire.
  const COUPON_TYPES: RewardTypeId[] = ['percentage', 'fixed', 'free_shipping', 'free_product'];
  const producesCoupon = COUPON_TYPES.includes(type);

  const pickProduct = async (): Promise<void> => {
    setError(null);
    const picker = window.shopify?.resourcePicker;
    if (!picker) {
      setError('Product picker is only available inside the Shopify admin.');
      return;
    }
    const selection = await picker({ type: 'product', action: 'select' });
    const chosen = selection?.[0];
    if (chosen) {
      setProductId(chosen.id);
      setProductTitle(chosen.title ?? 'Selected product');
      const img =
        chosen.images?.[0]?.originalSrc ??
        chosen.images?.[0]?.url ??
        chosen.images?.[0]?.src ??
        chosen.featuredImage?.originalSrc ??
        chosen.featuredImage?.url ??
        null;
      setProductImage(img);
    }
  };

  const add = async (): Promise<void> => {
    if (needsProduct && !productId) {
      setError('Select a product for this free-product reward.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const expiry = Number(expiryDays);
      await apiClient.post<RewardView>('/rewards', {
        campaignId,
        type,
        weight: Number(weight) || 1,
        ...(needsValue ? { value: Number(value) || 0 } : {}),
        ...(producesCoupon && expiry > 0 ? { expiryDays: Math.floor(expiry) } : {}),
        ...(needsProduct && productId
          ? { productId, ...(productTitle ? { productTitle } : {}), ...(productImage ? { productImage } : {}) }
          : {}),
      });
      setProductId(null);
      setProductTitle(null);
      setProductImage(null);
      setExpiryDays('');
      onChanged('Reward added');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not add reward');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (rewardId: string): Promise<void> => {
    try {
      await apiClient.del(`/rewards/${rewardId}`);
      onChanged('Reward removed');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not remove reward');
    }
  };

  return (
    <BlockStack gap="400">
      {error ? <ErrorState message={error} /> : null}
      <Card>
        <BlockStack gap="300">
          <Text as="h3" variant="headingSm">
            Add a reward
          </Text>
          <FormLayout>
            <FormLayout.Group>
              <Select label="Type" options={REWARD_TYPE_OPTIONS} value={type} onChange={(v) => setType(v as RewardTypeId)} />
              {needsValue ? (
                <TextField label="Value" type="number" value={value} onChange={setValue} autoComplete="off" />
              ) : null}
              <TextField label="Weight" type="number" value={weight} onChange={setWeight} autoComplete="off" />
              {producesCoupon ? (
                <TextField
                  label="Expiry (days)"
                  type="number"
                  value={expiryDays}
                  onChange={setExpiryDays}
                  autoComplete="off"
                  placeholder="No expiry"
                  helpText="Days the won code stays valid. Leave blank for no expiry."
                  min={1}
                />
              ) : null}
            </FormLayout.Group>
            {needsProduct ? (
              <InlineStack gap="300" blockAlign="center">
                <Button onClick={() => void pickProduct()}>
                  {productId ? 'Change product' : 'Select product'}
                </Button>
                <Text as="span" tone={productTitle ? 'success' : 'subdued'}>
                  {productTitle ?? 'No product selected — winners get this product free'}
                </Text>
              </InlineStack>
            ) : null}
          </FormLayout>
          <Button variant="primary" loading={busy} onClick={() => void add()}>
            Add reward
          </Button>
        </BlockStack>
      </Card>

      <Card>
        <BlockStack gap="300">
          <Text as="h3" variant="headingSm">
            Rewards ({rewards.length})
          </Text>
          {loading ? <LoadingState label="Loading rewards" /> : null}
          {rewards.length === 0 && !loading ? (
            <Text as="p" tone="subdued">
              No rewards yet. Add at least one so players can win.
            </Text>
          ) : null}
          {rewards.map((r) => (
            <InlineStack key={r.id} align="space-between" blockAlign="center">
              <Text as="span">
                {r.label ?? r.type} {r.value != null ? `(${r.value})` : ''}
                {r.productTitle ? ` — ${r.productTitle}` : ''} — weight {r.weight}
                {r.expiryDays ? ` — expires in ${r.expiryDays}d` : ''}
              </Text>
              <Button tone="critical" variant="plain" onClick={() => void remove(r.id)}>
                Remove
              </Button>
            </InlineStack>
          ))}
        </BlockStack>
      </Card>
    </BlockStack>
  );
}

function RulesTab({ campaignId, onSaved }: { campaignId: string; onSaved: (m: string) => void }): JSX.Element {
  const existing = useApi(async () => {
    try {
      return await apiClient.get<RulesView>(`/rules?campaignId=${campaignId}`);
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) return null;
      throw err;
    }
  }, [campaignId]);

  if (existing.loading) return <LoadingState label="Loading rules" />;
  return <RulesForm campaignId={campaignId} initial={existing.data ?? null} onSaved={onSaved} />;
}

function RulesForm({
  campaignId,
  initial,
  onSaved,
}: {
  campaignId: string;
  initial: RulesView | null;
  onSaved: (m: string) => void;
}): JSX.Element {
  const [pages, setPages] = useState<string[]>(initial?.displayRules?.pages ?? ['all']);
  const [customerType, setCustomerType] = useState<CustomerTypeId>(
    initial?.audienceRules?.customerType ?? 'any',
  );
  const [countries, setCountries] = useState((initial?.audienceRules?.countries ?? []).join(', '));
  const [devices, setDevices] = useState<string[]>(initial?.audienceRules?.devices ?? []);
  const [frequency, setFrequency] = useState<AttemptFrequencyId>(
    initial?.attemptRules?.frequency ?? 'one_time',
  );
  const [maxAttempts, setMaxAttempts] = useState(String(initial?.attemptRules?.maxAttempts ?? 1));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const showMax = frequency !== 'one_time' && frequency !== 'unlimited';

  const save = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    const countryList = countries.split(',').map((c) => c.trim().toUpperCase()).filter(Boolean);
    const config: RulesConfig = {
      displayRules: { pages },
      audienceRules: {
        customerType,
        ...(countryList.length ? { countries: countryList } : {}),
        ...(devices.length ? { devices } : {}),
      },
      attemptRules: {
        frequency,
        ...(showMax ? { maxAttempts: Number(maxAttempts) || 1 } : {}),
      },
    };
    try {
      await apiClient.post<RulesView>('/rules', { campaignId, ...config });
      onSaved('Rules saved');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save rules');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <BlockStack gap="400">
        {error ? <ErrorState message={error} /> : null}
        <ChoiceList
          allowMultiple
          title="Show on pages"
          choices={[
            { label: 'Entire store', value: 'all' },
            { label: 'Home', value: 'home' },
            { label: 'Product', value: 'product' },
            { label: 'Collection', value: 'collection' },
            { label: 'Cart', value: 'cart' },
          ]}
          selected={pages}
          onChange={setPages}
        />
        <Select
          label="Audience"
          options={[
            { label: 'Anyone', value: 'any' },
            { label: 'Guests only', value: 'guest' },
            { label: 'Logged-in only', value: 'logged_in' },
          ]}
          value={customerType}
          onChange={(v) => setCustomerType(v as CustomerTypeId)}
        />
        <TextField
          label="Countries (ISO codes, comma-separated; blank = all)"
          value={countries}
          onChange={setCountries}
          autoComplete="off"
          placeholder="US, CA, GB"
        />
        <ChoiceList
          allowMultiple
          title="Devices (blank = all)"
          choices={[
            { label: 'Desktop', value: 'desktop' },
            { label: 'Mobile', value: 'mobile' },
            { label: 'Tablet', value: 'tablet' },
          ]}
          selected={devices}
          onChange={setDevices}
        />
        <Select
          label="Attempts"
          options={[
            { label: 'One time', value: 'one_time' },
            { label: 'Daily', value: 'daily' },
            { label: 'Weekly', value: 'weekly' },
            { label: 'Monthly', value: 'monthly' },
            { label: 'Unlimited', value: 'unlimited' },
          ]}
          value={frequency}
          onChange={(v) => setFrequency(v as AttemptFrequencyId)}
        />
        {showMax ? (
          <TextField
            label="Max attempts per period"
            type="number"
            value={maxAttempts}
            onChange={setMaxAttempts}
            autoComplete="off"
          />
        ) : null}
        <Button variant="primary" loading={busy} onClick={() => void save()}>
          Save rules
        </Button>
      </BlockStack>
    </Card>
  );
}

function ScheduleTab({ campaign, onSaved }: { campaign: CampaignView; onSaved: (m: string) => void }): JSX.Element {
  const [publishAt, setPublishAt] = useState('');
  const [expireAt, setExpireAt] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async (): Promise<void> => {
    if (!publishAt) {
      setError('Set a publish date');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await apiClient.post<CampaignView>(`/campaigns/${campaign.id}/schedule`, {
        publishAt: new Date(publishAt).toISOString(),
        ...(expireAt ? { expireAt: new Date(expireAt).toISOString() } : {}),
      });
      onSaved('Campaign scheduled');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not schedule');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <BlockStack gap="400">
        {error ? <ErrorState message={error} /> : null}
        {campaign.status !== 'draft' ? (
          <Banner tone="info">
            <p>Scheduling applies to draft campaigns. This campaign is {campaign.status}.</p>
          </Banner>
        ) : null}
        <FormLayout>
          <TextField
            label="Publish at"
            type="datetime-local"
            value={publishAt}
            onChange={setPublishAt}
            autoComplete="off"
          />
          <TextField
            label="Expire at (optional)"
            type="datetime-local"
            value={expireAt}
            onChange={setExpireAt}
            autoComplete="off"
          />
        </FormLayout>
        <Button variant="primary" loading={busy} onClick={() => void save()}>
          Schedule campaign
        </Button>
      </BlockStack>
    </Card>
  );
}

function PublishTab({
  campaign,
  rewardCount,
  onPublished,
}: {
  campaign: CampaignView;
  rewardCount: number;
  onPublished: (m: string) => void;
}): JSX.Element {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const warnings: string[] = [];
  if (!campaign.gameId) warnings.push('No game selected — players will see the default Spin Wheel.');
  if (rewardCount === 0) warnings.push('No rewards configured — players cannot win anything.');

  const publish = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      await apiClient.post<CampaignView>(`/campaigns/${campaign.id}/publish`);
      onPublished('Campaign published');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not publish');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <BlockStack gap="400">
        {error ? <ErrorState message={error} /> : null}
        <Text as="h3" variant="headingSm">
          Review
        </Text>
        <List>
          <List.Item>Name: {campaign.campaignName}</List.Item>
          <List.Item>Status: {campaign.status}</List.Item>
          <List.Item>Game: {campaign.gameId ? 'configured' : 'default (Spin Wheel)'}</List.Item>
          <List.Item>Rewards: {rewardCount}</List.Item>
        </List>
        {warnings.length > 0 ? (
          <Banner tone="warning" title="Before you publish">
            <List>
              {warnings.map((w) => (
                <List.Item key={w}>{w}</List.Item>
              ))}
            </List>
          </Banner>
        ) : null}
        <ButtonGroup>
          <Button
            variant="primary"
            loading={busy}
            disabled={campaign.status === 'published'}
            onClick={() => void publish()}
          >
            {campaign.status === 'published' ? 'Published' : 'Publish campaign'}
          </Button>
        </ButtonGroup>
      </BlockStack>
    </Card>
  );
}
